import { BlankModal, Flex, ModalBody, ModalFooter, ModalHeader, useToast } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useFullscreenModalControls } from '../../../../modals/context/modal-context-provider';
import { useMergeEventApi } from '../../../hooks/use-merge-event-api';
import {
    ManageEventMergeManager,
    ManageEventMergeModalError,
} from './manage-event-merge-manager/manage-event-merge-manager';
import {
    ManageEventMergeManagerFooterSkeleton,
    ManageEventMergeManagerHeaderSkeleton,
    ManageEventMergeModalBodySkeleton,
} from './manage-event-merge-manager/manage-event-merge-skeleton';
import { createApiErrorHandler } from '@m43/core';
import { useManageEventMergeStore } from '../hooks/use-manage-event-merge-store';
import { ManageEventMergeStore } from '../store/manage-merge-event-store';
import { MergeEventView } from '@mark43/cad-api';
import { ManageEventMergeManagerHeader } from './manage-event-merge-manager/manage-event-merge-manager-header';
import { ManageEventMergeManagerFooter } from './manage-event-merge-manager/manage-event-merge-manager-footer';
import { useSubmitFormOnEnter } from '../../../../common/hooks/use-submit-form-on-enter';

interface MergeEventModalProps {
    primaryEventParameters: {
        id: number;
    };
}

export type ManageEventMergeModalComponentType = typeof ManageEventMergeModal;

const defaultMergingError = {
    title: 'Merging Error',
    message: 'Try again or contact an administrator.',
};

export const unmergeCommentRequiredError = {
    title: 'Comment Required',
    message: 'Unmerging comments are required.',
};

export const ManageEventMergeModal: React.FC<MergeEventModalProps> = observer(
    ({ primaryEventParameters }) => {
        const [isDoingMergeOperation, setIsDoingMergeOperation] = useState(false);
        const [currentUnmergeReason, setCurrentUnmergeReason] = useState('');
        const [error, setError] = useState<ManageEventMergeModalError | null>(null);
        const [modalContentElement, setModalContentElement] = useState<HTMLElement | null>(null);

        const modalControls = useFullscreenModalControls();
        const { updateEvents } = useMergeEventApi();
        const { store } = useManageEventMergeStore(primaryEventParameters.id);
        const toast = useToast();

        const isLoaded = store !== undefined;

        const mergeEvents = store ? store?.getMergeItems() : ([] as MergeEventView[]);
        const unmergeEvents = store ? store?.getUnmergeItems() : ([] as MergeEventView[]);

        const canSubmit =
            isLoaded &&
            !isDoingMergeOperation &&
            (mergeEvents.length !== 0 || unmergeEvents.length !== 0) &&
            !(mergeEvents.length !== 0 && store.currentPrimaryEvent.wasCleared);

        function onUpdateUnmergeComment(comment: string): void {
            if (
                store?.getUnmergeItems().length !== 0 &&
                currentUnmergeReason !== '' &&
                comment === ''
            ) {
                setError(unmergeCommentRequiredError);
            } else {
                setError(null);
            }

            setCurrentUnmergeReason(comment);
        }

        function createToastDescription(
            eventsBeingMerged: MergeEventView[],
            eventsBeingUnmerged: MergeEventView[]
        ): string {
            const mergeDescription = `Event ${eventsBeingMerged
                .map((e) =>
                    e.eventNumber !== undefined
                        ? `#${e.eventNumber.substring(e.eventNumber.length - 4)}`
                        : ''
                )
                .join(', ')} successfully merged`;

            const unmergeDescription = `Events ${eventsBeingUnmerged
                .map((e) =>
                    e.eventNumber !== undefined
                        ? `#${e.eventNumber.substring(e.eventNumber.length - 4)}`
                        : ''
                )
                .join(', ')} successfully unmerged`;

            if (eventsBeingMerged.length !== 0 && eventsBeingUnmerged.length !== 0) {
                return `${mergeDescription}, ${unmergeDescription}`;
            }

            if (eventsBeingMerged.length !== 0) {
                return mergeDescription;
            }

            return unmergeDescription;
        }

        function doMergeUnmergeApiCall(store: ManageEventMergeStore): void {
            const eventsBeingMerged = [...store.getMergeItemsForApi()];
            const eventsBeingUnmerged = [...store.getUnmergeItems()];
            updateEvents({
                parentAgencyEventId: store.currentPrimaryEvent.event.id,
                childAgencyEventIdsToLink: eventsBeingMerged.map((e) => e.id),
                childAgencyEventIdsToUnlink: eventsBeingUnmerged.map((e) => e.id),
                reason: currentUnmergeReason,
            })
                .then(() => {
                    toast({
                        description: createToastDescription(eventsBeingMerged, eventsBeingUnmerged),
                        status: 'default',
                        duration: 3000,
                    });
                    modalControls.close();
                })
                .catch(createApiErrorHandler(() => setError(defaultMergingError)))
                .finally(() => setIsDoingMergeOperation(false));
        }

        function onMergeUnmergeEvents(): void {
            if (unmergeEvents.length !== 0 && currentUnmergeReason.trim() === '') {
                setError(unmergeCommentRequiredError);
                return;
            }
            if (store !== undefined) {
                setError(null);
                setIsDoingMergeOperation(true);
                doMergeUnmergeApiCall(store);
            }
        }

        function onCancel(): void {
            modalControls.close();
        }

        useSubmitFormOnEnter({
            element: modalContentElement,
            onSubmit: onMergeUnmergeEvents,
        });

        return (
            <BlankModal
                size="xl"
                scrollBehavior="inside"
                isOpen={true}
                onClose={onCancel}
                isCloseable={!isDoingMergeOperation}
            >
                <ModalHeader>
                    {isLoaded ? (
                        <ManageEventMergeManagerHeader store={store} />
                    ) : (
                        <ManageEventMergeManagerHeaderSkeleton />
                    )}
                </ModalHeader>
                <ModalBody style={{ display: 'flex', flexDirection: 'column' }}>
                    <Flex direction="column" style={{ minHeight: 0 }} ref={setModalContentElement}>
                        {isLoaded ? (
                            // Manager class with observer is needed for GroupedVirtuoso to responsively re-render store changes
                            <ManageEventMergeManager
                                store={store}
                                interactionDisabled={isDoingMergeOperation}
                                error={error}
                                onSetUnmergeComments={onUpdateUnmergeComment}
                            />
                        ) : (
                            <ManageEventMergeModalBodySkeleton />
                        )}
                    </Flex>
                </ModalBody>
                <ModalFooter>
                    {isLoaded ? (
                        <ManageEventMergeManagerFooter
                            onSave={onMergeUnmergeEvents}
                            onCancel={onCancel}
                            isLoading={isDoingMergeOperation}
                            canSubmit={canSubmit}
                        />
                    ) : (
                        <ManageEventMergeManagerFooterSkeleton />
                    )}
                </ModalFooter>
            </BlankModal>
        );
    }
);
